// 数据库服务
import Dexie, { Table } from 'dexie';

// 计时器数据接口
interface TimerData {
  id: string;
  name: string;
  hours: number;
  minutes: number;
  seconds: number;
  mode: 'countdown' | 'stopwatch';
  showMilliseconds: boolean;
  createdAt: number;
  effectType?: string;
  effectOpacity?: number;
  effectColor?: string;
  effectSecondaryColor?: string;
  effectSpeed?: number;
}

// 设置数据接口
interface SettingsData {
  id?: number;
  key: string;
  value: any;
}

// 定义数据库类
class TimerAppDB extends Dexie {
  timers!: Table<TimerData>;
  settings!: Table<SettingsData>;

  constructor() {
    super('timer-app-db');
    this.version(1).stores({
      timers: 'id, createdAt',
      settings: '++id, key'
    });
  }
}

// 创建数据库实例
const db = new TimerAppDB();

// 设置相关操作
const getSettings = async () => {
  // 获取所有设置
  const darkModeSetting = await db.settings.where('key').equals('darkMode').first();
  const showMillisecondsSetting = await db.settings.where('key').equals('showMilliseconds').first();
  const notificationSoundSetting = await db.settings.where('key').equals('notificationSound').first();
  const notificationsEnabledSetting = await db.settings.where('key').equals('notificationsEnabled').first();
  const languageSetting = await db.settings.where('key').equals('language').first();
  
  return {
    darkMode: darkModeSetting?.value ?? true,
    showMilliseconds: showMillisecondsSetting?.value ?? false,
    notificationSound: notificationSoundSetting?.value ?? 'default',
    notificationsEnabled: notificationsEnabledSetting?.value ?? true,
    language: languageSetting?.value ?? 'en',
  };
};

const updateSettings = async (changes: Record<string, any>) => {
  // 开始事务
  await db.transaction('rw', db.settings, async () => {
    // 更新每个设置项
    for (const [key, value] of Object.entries(changes)) {
      // 检查设置是否存在
      const existingSetting = await db.settings.where('key').equals(key).first();
      
      if (existingSetting) {
        // 更新现有设置
        await db.settings.update(existingSetting.id!, { value });
      } else {
        // 创建新设置
        await db.settings.add({ key, value });
      }
    }
  });
  
  return changes;
};

// 计时器相关操作
const getTimers = async () => {
  return await db.timers.toArray();
};

const addTimer = async (timer: Omit<TimerData, 'id' | 'createdAt'>) => {
  const id = crypto.randomUUID();
  const createdAt = Date.now();
  
  const newTimer = {
    ...timer,
    id,
    createdAt,
  };
  
  await db.timers.add(newTimer);
  return newTimer;
};

const updateTimer = async (id: string, changes: Partial<Omit<TimerData, 'id' | 'createdAt'>>) => {
  const timer = await db.timers.get(id);
  if (!timer) throw new Error(`Timer with id ${id} not found`);
  
  const updatedTimer = {
    ...timer,
    ...changes,
  };
  
  await db.timers.update(id, changes);
  return updatedTimer;
};

const deleteTimer = async (id: string) => {
  await db.timers.delete(id);
};

// 导出数据库服务
const dbService = {
  getSettings,
  updateSettings,
  getTimers,
  addTimer,
  updateTimer,
  deleteTimer,
};

export default dbService;